import {
  EXPERIENCE_CONTRACT_FIXTURE,
  normalizeExperienceContract,
  type ExperienceContract,
} from "@/lib/experience-contract";

const CSRF_COOKIE = "__Host-alloy_csrf";

function getCsrfToken(): string | null {
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${CSRF_COOKIE}=`));
  return match ? decodeURIComponent(match.split("=")[1]) : null;
}

function correlationId(scope = "api"): string {
  return `corr_sandbox_ui_${scope}_${Date.now()}`;
}

interface ApiFetchResult {
  response: Response;
  correlationId: string;
}

async function apiFetchWithCorrelation(
  path: string,
  options: RequestInit = {},
  scope?: string,
): Promise<ApiFetchResult> {
  const requestCorrelationId = correlationId(scope);
  const headers = new Headers(options.headers);
  headers.set("accept", "application/json");
  headers.set("x-alloy-correlation-id", requestCorrelationId);

  const csrf = getCsrfToken();
  if (csrf && options.method && options.method !== "GET") {
    headers.set("x-alloy-csrf-token", csrf);
  }

  const response = await fetch(path, {
    ...options,
    cache: "no-store" as RequestCache,
    headers,
    credentials: "same-origin",
  });
  return { response, correlationId: requestCorrelationId };
}

export async function apiFetch(
  path: string,
  options: RequestInit = {},
  scope?: string,
): Promise<Response> {
  const { response } = await apiFetchWithCorrelation(path, options, scope);
  return response;
}

// --- Auth ---

export interface SessionInfo {
  authenticated: boolean;
  email?: string;
  roleNames?: string[];
  [key: string]: unknown;
}

export async function checkSession(): Promise<SessionInfo> {
  try {
    const res = await apiFetch("/auth/session", {}, "session");
    if (!res.ok) return { authenticated: false };
    const data = await res.json();
    if (!data || !data.authenticated) return { authenticated: false };
    return data as SessionInfo;
  } catch {
    return { authenticated: false };
  }
}

export interface AuthMethod {
  id: string;
  label: string;
  detail: string;
  startUrl: string;
  enabled: boolean;
  primary: boolean;
}

function safeSameOriginPath(value: unknown): string {
  if (typeof value !== "string" || !value.startsWith("/")) return "";
  if (value.startsWith("//") || value.includes("\\") || value.includes("\n") || value.includes("\r")) return "";
  return value.slice(0, 240);
}

function boundedText(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, 96) : fallback;
}

function loginPathFor(customerId: string, providerId: string, loginPath?: string, returnTo?: string): string {
  const path = safeSameOriginPath(loginPath || "/auth/login");
  if (!path) return "";
  const query = new URLSearchParams();
  query.set("return_to", returnTo || "/");
  if (customerId) query.set("customer", customerId);
  if (providerId) query.set("provider", providerId);
  return path + "?" + query.toString();
}

const DEFAULT_AUTH_METHODS: AuthMethod[] = [
  {
    id: "google-oidc",
    label: "Continue with Google",
    detail: "Google Workspace OIDC single sign-on",
    startUrl: "/auth/login?return_to=/",
    enabled: true,
    primary: true,
  },
];

function parseAuthMethods(config: Record<string, unknown>, returnTo?: string): AuthMethod[] {
  const customers = Array.isArray(config.customers) ? config.customers : [];
  const methods: AuthMethod[] = [];

  for (const customer of customers) {
    if (!customer || typeof customer !== "object") continue;
    const customerId = typeof (customer as Record<string, unknown>).customerId === "string"
      ? ((customer as Record<string, unknown>).customerId as string).trim()
      : "";
    const providers = Array.isArray((customer as Record<string, unknown>).providers)
      ? (customer as Record<string, unknown>).providers as Record<string, unknown>[]
      : [];

    for (const provider of providers) {
      if (!provider || typeof provider !== "object") continue;
      const providerId = typeof provider.providerId === "string" ? provider.providerId.trim() : "";
      const type = boundedText(provider.type, "oidc").toLowerCase();
      const browserStartSupported = provider.browserStartSupported === true;
      const enabled = provider.enabled === true && provider.browserLoginEnabled === true && browserStartSupported;
      const displayName = boundedText(provider.displayName, "");
      const isGoogle = /google/i.test(displayName) || providerId === "google";

      methods.push({
        id: providerId === "google" ? "google-oidc" : `provider-${providerId || methods.length + 1}`,
        label: isGoogle ? "Continue with Google" : (displayName || (type === "saml" ? "Enterprise SAML" : "Enterprise SSO")),
        detail: browserStartSupported && type === "oidc"
          ? "Gateway-managed OIDC single sign-on"
          : type === "saml"
            ? "Configured for this tenant; browser start is not enabled"
            : "Configured for this tenant; not enabled for browser login",
        startUrl: enabled ? loginPathFor(customerId, providerId, provider.loginPath as string | undefined, returnTo) : "",
        enabled,
        primary: isGoogle || methods.length === 0,
      });
    }
  }

  if (Array.isArray(config.authMethods)) {
    for (const method of config.authMethods as Record<string, unknown>[]) {
      if (!method || typeof method !== "object" || typeof method.id !== "string") continue;
      methods.push({
        id: method.id,
        label: boundedText(method.label, "Sign in"),
        detail: boundedText(method.detail, ""),
        startUrl: safeSameOriginPath(method.startUrl),
        enabled: method.enabled === true,
        primary: method.primary === true,
      });
    }
  }

  const best = new Map<string, AuthMethod>();
  for (const m of methods) {
    const existing = best.get(m.id);
    if (!existing) { best.set(m.id, m); continue; }
    const score = (x: AuthMethod) => (x.enabled ? 2 : 0) + (x.primary ? 1 : 0);
    if (score(m) > score(existing)) best.set(m.id, m);
  }
  return Array.from(best.values());
}

export async function fetchAuthOptions(returnTo?: string): Promise<AuthMethod[]> {
  try {
    const res = await apiFetch("/auth/options", {}, "auth_config");
    if (!res.ok) return DEFAULT_AUTH_METHODS;
    const config = await res.json();
    if (!config || typeof config !== "object") return DEFAULT_AUTH_METHODS;
    const methods = parseAuthMethods(config as Record<string, unknown>, returnTo);
    return methods.length > 0 ? methods : DEFAULT_AUTH_METHODS;
  } catch {
    return DEFAULT_AUTH_METHODS;
  }
}

export async function logout(): Promise<void> {
  await apiFetch("/auth/logout", { method: "POST" }, "logout");
}

// --- Shared sandbox org/environment context ---

export async function fetchExperienceContract(): Promise<ExperienceContract> {
  try {
    const res = await apiFetch("/v1/session/effective-access", {}, "effective_access");
    if (!res.ok) return EXPERIENCE_CONTRACT_FIXTURE;
    const data = await res.json();
    return normalizeExperienceContract(data, "api");
  } catch {
    return EXPERIENCE_CONTRACT_FIXTURE;
  }
}

export async function setActiveOrganization(organizationId: string): Promise<ExperienceContract> {
  try {
    const res = await apiFetch("/v1/session/active-organization", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ organizationId }),
    }, "active_org");
    if (!res.ok) return EXPERIENCE_CONTRACT_FIXTURE;
    const data = await res.json();
    return normalizeExperienceContract(data, "api");
  } catch {
    return EXPERIENCE_CONTRACT_FIXTURE;
  }
}

// --- Wallets ---

export interface ApiErrorInfo {
  reasonCode: string;
  supportReference?: string;
}

export interface WalletListResult {
  wallets: Record<string, unknown>[];
  error?: ApiErrorInfo;
}

function boundedErrorText(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, 160) : undefined;
}

function responseReasonCode(data: Record<string, unknown>): string | undefined {
  return boundedErrorText(data.reason_code)
    || boundedErrorText(data.reasonCode);
}

function responseSupportReference(res: Response, data: Record<string, unknown>): string | undefined {
  return boundedErrorText(data.supportReference)
    || boundedErrorText(data.support_reference)
    || boundedErrorText(res.headers.get("X-Alloy-Support-Reference"));
}

function extractApiError(res: Response, data: Record<string, unknown>, fallbackCorrelationId?: string): ApiErrorInfo {
  return {
    reasonCode: responseReasonCode(data)
      || boundedErrorText(data.error)
      || boundedErrorText(data.message)
      || `HTTP_${res.status}`,
    supportReference: responseSupportReference(res, data) || fallbackCorrelationId,
  };
}

export interface ApiListResponse<T = Record<string, unknown>> {
  items: T[];
  total?: number;
  error?: ApiErrorInfo;
}

export async function fetchList<T = Record<string, unknown>>(
  path: string,
  listKey: string,
  scope: string,
): Promise<ApiListResponse<T>> {
  const { response: res, correlationId: cid } = await apiFetchWithCorrelation(path, {}, scope);
  const data = await res.json();
  if (!res.ok) {
    const body = data && typeof data === "object" ? data as Record<string, unknown> : {};
    return { items: [], error: extractApiError(res, body, cid) };
  }
  const items = Array.isArray(data[listKey]) ? data[listKey]
    : Array.isArray(data) ? data
    : [];
  return { items: items as T[], total: typeof data.total === "number" ? data.total : undefined };
}

export interface ApiDetailResponse<T = Record<string, unknown>> {
  item: T | null;
  error?: ApiErrorInfo;
}

export async function fetchDetail<T = Record<string, unknown>>(
  path: string,
  scope: string,
): Promise<ApiDetailResponse<T>> {
  const { response: res, correlationId: cid } = await apiFetchWithCorrelation(path, {}, scope);
  const data = await res.json();
  if (!res.ok) {
    const body = data && typeof data === "object" ? data as Record<string, unknown> : {};
    return { item: null, error: extractApiError(res, body, cid) };
  }
  return { item: data as T };
}

export interface ApiMutationResult {
  ok: boolean;
  error?: string;
  data?: Record<string, unknown>;
}

export async function apiMutate(
  path: string,
  method: string,
  body: unknown,
  scope: string,
): Promise<ApiMutationResult> {
  try {
    const res = await apiFetch(path, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }, scope);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        ok: false,
        error: (data as Record<string, string>)?.message
          || (data as Record<string, string>)?.reason_code
          || (data as Record<string, string>)?.reasonCode
          || `HTTP ${res.status}`,
      };
    }
    return { ok: true, data: data as Record<string, unknown> };
  } catch {
    return { ok: false, error: "Network error" };
  }
}

export async function fetchWallets(): Promise<WalletListResult> {
  const { response: res, correlationId: requestCorrelationId } = await apiFetchWithCorrelation(
    "/v1/walletkit/wallets",
    {},
    "wallet_list",
  );
  const data = await res.json();
  if (!res.ok) {
    const body = data && typeof data === "object" ? data as Record<string, unknown> : {};
    return {
      wallets: [],
      error: {
        reasonCode: responseReasonCode(body) || boundedErrorText(body.error) || `HTTP_${res.status}`,
        supportReference: responseSupportReference(res, body) || requestCorrelationId,
      },
    };
  }
  return { wallets: data.wallets || [] };
}

export interface WalletOptionsResult {
  networks: { chain: string; network: string; enabled: boolean }[];
  providerProfiles: { walletProfileId: string; displayName: string; enabled: boolean }[];
}

export async function fetchWalletOptions(): Promise<WalletOptionsResult | null> {
  try {
    const res = await apiFetch("/v1/wallets/new", {}, "wallet_options");
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export async function createWallet(
  accountAlias: string,
  opts?: { chain?: string; network?: string; walletProfileId?: string },
): Promise<{ ok: boolean; walletId?: string; error?: string }> {
  const body: Record<string, string> = { accountAlias };
  if (opts?.chain) body.chain = opts.chain;
  if (opts?.network) body.network = opts.network;
  if (opts?.walletProfileId) body.walletProfileId = opts.walletProfileId;

  const res = await apiFetch("/v1/walletkit/wallets", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }, "wallet_create");
  const data = await res.json();
  if (!res.ok) {
    return { ok: false, error: data?.message || data?.reason_code || data?.reasonCode || "Create failed" };
  }
  return { ok: true, walletId: data.walletId };
}

// --- Bootstrap ---

export async function previewBootstrapInvite(rawInviteToken: string): Promise<Record<string, unknown> | null> {
  try {
    const res = await apiFetch("/auth/bootstrap/bootstrap-invites/preview", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rawInviteToken }),
    }, "bootstrap_preview");
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export async function acceptBootstrapInvite(rawInviteToken: string): Promise<{ ok: boolean; organizationId?: string; error?: string }> {
  try {
    const res = await apiFetch("/auth/bootstrap/bootstrap-invites/accept", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rawInviteToken }),
    }, "bootstrap_accept");
    const data = await res.json();
    if (!res.ok) {
      return { ok: false, error: data?.message || "Activation failed" };
    }
    return { ok: true, organizationId: data.organizationId };
  } catch {
    return { ok: false, error: "Activation failed" };
  }
}

// --- Transfers ---

export async function fetchTransfers(): Promise<{ transfers: Record<string, unknown>[]; reasonCode?: string }> {
  const res = await apiFetch("/v1/transfers", {}, "transfer_list");
  const data = await res.json();
  if (!res.ok) {
    return { transfers: [], reasonCode: data?.reason_code || data?.reasonCode || data?.message || `HTTP ${res.status}` };
  }
  return { transfers: Array.isArray(data.transfers) ? data.transfers : Array.isArray(data) ? data : [] };
}

export async function fetchTransferDetail(id: string): Promise<{ transfer: Record<string, unknown> | null; reasonCode?: string }> {
  const res = await apiFetch(`/v1/transfers/${encodeURIComponent(id)}`, {}, "transfer_detail");
  const data = await res.json();
  if (!res.ok) {
    return { transfer: null, reasonCode: data?.reason_code || data?.reasonCode || data?.message || `HTTP ${res.status}` };
  }
  return { transfer: data };
}

export async function fetchTransferEstimate(params: {
  sourceWalletId: string;
  destinationAddress: string;
  asset: string;
  amount: string;
}): Promise<{ estimate: Record<string, unknown> | null; error?: string }> {
  try {
    const res = await apiFetch("/v1/transfers/estimate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(params),
    }, "transfer_estimate");
    const data = await res.json();
    if (!res.ok) return { estimate: null, error: data?.message || "Estimate failed" };
    return { estimate: data };
  } catch {
    return { estimate: null, error: "Estimate unavailable" };
  }
}

export async function createTransferDraft(draft: Record<string, unknown>): Promise<{ ok: boolean; transferId?: string; error?: string }> {
  const res = await apiFetch("/v1/transfers/drafts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(draft),
  }, "transfer_draft_create");
  const data = await res.json();
  if (!res.ok) {
    return { ok: false, error: data?.message || data?.reason_code || data?.reasonCode || "Draft creation failed" };
  }
  return { ok: true, transferId: data.transferId || data.id };
}

export async function submitTransferDraft(draftId: string): Promise<{ ok: boolean; error?: string }> {
  const res = await apiFetch(`/v1/transfers/drafts/${encodeURIComponent(draftId)}/submit`, {
    method: "POST",
  }, "transfer_draft_submit");
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: (data as Record<string, string>)?.message || "Submit failed" };
  }
  return { ok: true };
}

// --- Approvals ---

export async function fetchApprovals(): Promise<{ approvals: Record<string, unknown>[]; reasonCode?: string }> {
  const res = await apiFetch("/v1/approvals", {}, "approval_list");
  const data = await res.json();
  if (!res.ok) {
    return { approvals: [], reasonCode: data?.reason_code || data?.reasonCode || data?.message || `HTTP ${res.status}` };
  }
  return { approvals: Array.isArray(data.approvals) ? data.approvals : Array.isArray(data) ? data : [] };
}

export async function fetchApprovalDetail(id: string): Promise<{ approval: Record<string, unknown> | null; reasonCode?: string }> {
  const res = await apiFetch(`/v1/approvals/${encodeURIComponent(id)}`, {}, "approval_detail");
  const data = await res.json();
  if (!res.ok) {
    return { approval: null, reasonCode: data?.reason_code || data?.reasonCode || data?.message || `HTTP ${res.status}` };
  }
  return { approval: data };
}

export async function approveApproval(id: string, comment?: string): Promise<{ ok: boolean; error?: string }> {
  const res = await apiFetch(`/v1/approvals/${encodeURIComponent(id)}/approve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ comment }),
  }, "approval_approve");
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: (data as Record<string, string>)?.message || "Approve failed" };
  }
  return { ok: true };
}

export async function rejectApproval(id: string, reason: string): Promise<{ ok: boolean; error?: string }> {
  const res = await apiFetch(`/v1/approvals/${encodeURIComponent(id)}/reject`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason }),
  }, "approval_reject");
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, error: (data as Record<string, string>)?.message || "Reject failed" };
  }
  return { ok: true };
}

// --- Wallet detail ---

export async function fetchWalletDetail(id: string): Promise<{ wallet: Record<string, unknown> | null; reasonCode?: string }> {
  try {
    const res = await apiFetch(`/v1/wallets/${encodeURIComponent(id)}`, {}, "wallet_detail");
    const data = await res.json();
    if (res.ok) return { wallet: data };
  } catch { /* fall through to list fallback */ }

  try {
    const listResult = await fetchWallets();
    const match = listResult.wallets.find(
      (w: Record<string, unknown>) => w.walletId === id || w.id === id,
    );
    if (match) return { wallet: match };
  } catch { /* ignore */ }

  return { wallet: null, reasonCode: "Wallet not found" };
}

// --- Notifications ---

export async function fetchNotifications(): Promise<{ notifications: Record<string, unknown>[]; reasonCode?: string }> {
  try {
    const res = await apiFetch("/v1/notifications", {}, "notification_list");
    const data = await res.json();
    if (!res.ok) return { notifications: [], reasonCode: data?.reason_code || data?.reasonCode || `HTTP ${res.status}` };
    return { notifications: Array.isArray(data.notifications) ? data.notifications : Array.isArray(data) ? data : [] };
  } catch {
    return { notifications: [], reasonCode: "Network error" };
  }
}

export async function markNotificationRead(id: string): Promise<{ ok: boolean }> {
  try {
    const res = await apiFetch(`/v1/notifications/${encodeURIComponent(id)}/read`, { method: "POST" }, "notification_read");
    return { ok: res.ok };
  } catch {
    return { ok: false };
  }
}

// --- Config ---

export async function fetchConfig(): Promise<{ config: Record<string, unknown> | null; error?: string }> {
  try {
    const res = await apiFetch("/v1/config/settings", {}, "config_settings");
    const data = await res.json();
    if (!res.ok) return { config: null, error: data?.reason_code || data?.reasonCode || `HTTP ${res.status}` };
    return { config: data };
  } catch {
    return { config: null, error: "Network error" };
  }
}

// --- Capabilities ---

export async function fetchCapabilities(): Promise<{ capabilities: Record<string, unknown> | null; error?: string }> {
  try {
    const res = await apiFetch("/v1/capabilities", {}, "capabilities");
    const data = await res.json();
    if (!res.ok) return { capabilities: null, error: `HTTP ${res.status}` };
    return { capabilities: data };
  } catch {
    return { capabilities: null, error: "Network error" };
  }
}

// --- Admin Users ---

export interface AdminUser {
  principalId: string;
  principalType: string;
  displayName: string;
  email: string;
  status: string;
  createdAt: string;
  ownerPrincipalId?: string;
  roleName?: string;
  mfaMethod?: string;
  groupNames?: string;
}

export interface AdminGroup {
  groupId: string;
  name: string;
  description: string;
  status: string;
  memberCount: number;
}

export interface AdminSession {
  sessionIdSuffix: string;
  authMethod: string;
  mfaPresent: boolean;
  assuranceLevel: string;
  channel: string;
  createdAt: string;
  lastSeenAt: string | null;
  idleExpiresAt: string | null;
}

export interface AuditEvent {
  eventId: string;
  eventType: string;
  outcome: string;
  authMethod: string;
  assuranceLevel: string;
  mfaPresent: boolean;
  occurredAt: string | null;
}

export interface AdminUsersResult {
  users: AdminUser[];
  total: number;
  error?: string;
}

export async function fetchAdminUsers(): Promise<AdminUsersResult> {
  try {
    const res = await apiFetch("/v1/admin/users", {}, "admin_users");
    if (!res.ok) return { users: [], total: 0, error: `HTTP ${res.status}` };
    const data = await res.json();
    return { users: data.users || [], total: data.total || 0 };
  } catch {
    return { users: [], total: 0, error: "Network error" };
  }
}

export async function fetchAdminUserDetail(userId: string): Promise<{ user: AdminUser | null; error?: string }> {
  try {
    const res = await apiFetch(`/v1/admin/users/${encodeURIComponent(userId)}`, {}, "admin_user_detail");
    if (!res.ok) return { user: null, error: `HTTP ${res.status}` };
    const data = await res.json();
    return { user: data };
  } catch {
    return { user: null, error: "Network error" };
  }
}

export interface AdminInvite {
  inviteId: string;
  email: string;
  displayName: string;
  status: string;
  deliveryStatus: string;
  expiresAt: string;
  createdAt: string;
  actions?: { canResend: boolean; canRevoke: boolean };
}

export async function fetchAdminInvites(): Promise<{ invites: AdminInvite[]; error?: string }> {
  try {
    const res = await apiFetch("/v1/admin/invites", {}, "admin_invites");
    if (!res.ok) return { invites: [], error: `HTTP ${res.status}` };
    const data = await res.json();
    return { invites: data.invites || [] };
  } catch {
    return { invites: [], error: "Network error" };
  }
}

export async function createAdminInvite(invite: {
  email: string;
  displayName: string;
  roleName: string;
  mfaRequirement?: string;
  environmentScope?: string;
  expiresInHours?: number;
}): Promise<{ ok: boolean; inviteId?: string; error?: string }> {
  try {
    const res = await apiFetch("/v1/admin/invites", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(invite),
    }, "admin_invite_create");
    const data = await res.json();
    if (!res.ok) return { ok: false, error: data?.message || data?.reasonCode || "Invite failed" };
    return { ok: true, inviteId: data.inviteId };
  } catch {
    return { ok: false, error: "Network error" };
  }
}

export async function suspendAdminUser(userId: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await apiFetch(`/v1/admin/users/${encodeURIComponent(userId)}/suspend`, {
      method: "POST",
    }, "admin_user_suspend");
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return { ok: false, error: (data as Record<string, string>)?.message || "Suspend failed" };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: "Network error" };
  }
}

export async function fetchAdminGroups(): Promise<{ groups: AdminGroup[]; error?: string }> {
  try {
    const res = await apiFetch("/v1/admin/groups", {}, "admin_groups");
    if (!res.ok) return { groups: [], error: `HTTP ${res.status}` };
    const data = await res.json();
    return { groups: data.groups || [] };
  } catch {
    return { groups: [], error: "Network error" };
  }
}

export async function fetchUserSessions(userId: string): Promise<{ sessions: AdminSession[]; error?: string }> {
  try {
    const res = await apiFetch(`/v1/admin/users/${encodeURIComponent(userId)}/sessions`, {}, "admin_user_sessions");
    if (!res.ok) return { sessions: [], error: `HTTP ${res.status}` };
    const data = await res.json();
    return { sessions: data.sessions || [] };
  } catch {
    return { sessions: [], error: "Network error" };
  }
}

export async function fetchUserLoginHistory(userId: string): Promise<{ events: AuditEvent[]; error?: string }> {
  try {
    const res = await apiFetch(`/v1/admin/users/${encodeURIComponent(userId)}/login-history`, {}, "admin_user_login_history");
    if (!res.ok) return { events: [], error: `HTTP ${res.status}` };
    const data = await res.json();
    return { events: data.events || [] };
  } catch {
    return { events: [], error: "Network error" };
  }
}

// --- Admin Roles ---

export interface AdminRoleDefinition {
  roleDefinitionId: string;
  organizationId: string;
  tenantId: string;
  environment: string;
  trustDomain: string;
  roleName: string;
  displayName: string;
  status: string;
  scopes: string[];
  permissions: string[];
}

export async function fetchAdminRoles(): Promise<{ roles: AdminRoleDefinition[]; error?: string }> {
  try {
    const res = await apiFetch("/v1/admin/roles", {}, "admin_roles");
    if (!res.ok) return { roles: [], error: `HTTP ${res.status}` };
    const data = await res.json();
    return { roles: data.roles || [] };
  } catch {
    return { roles: [], error: "Network error" };
  }
}
